import * as React from 'react';
import { t as translate } from '@reos/i18n';
import { canonicalCzechNumber, formatCzechNumber, readCzechNumberInput } from '../lib/czech-number.js';
import { useLocaleOrDefault } from '../lib/i18n.js';

/*
 * Krok UXD2 — JEDNO CISELNE POLE PRO CELOU ADMINISTRACI (mimo editor smlouvy).
 *
 * Proc ne `<input type="number">`: prohlizec do nativniho pole nepusti ani
 * mezeru, ani desetinnou carku, takze z prirozeneho ceskeho zapisu
 * `1 250 000,50` zustane `125000050` — a ulozi se to BEZ VAROVANI jako
 * castka. Presne tohle nasel zivy pruchod EDIT1 u najemneho (nalez B1).
 * Krok EDIT2 to opravil POUZE v editoru smlouvy; mimo nej zustala pole bud
 * nativni `number`, nebo surovy text, ktery sel do API tak, jak ho clovek
 * napsal (faktura posilala `unitPriceWithoutVat: "1 250 000,50"`).
 *
 * Tohle je SKORAPKA, ne druhy prevod. Cte i pise `lib/czech-number.ts`,
 * tedy tentyz parser, jaky uz ma editor smlouvy; rozhodnuti „rozepsane /
 * nesrozumitelne / hotove" dela `readCzechNumberInput`. Odlisnost od
 * `NumericFieldInput` v `contracts.tsx` je JEDINA: tam se pole uklada samo
 * 450 ms po poslednim znaku (`DraftTextInput`), tady drzi hodnotu formular
 * a uklada se tlacitkem. Proto dve skorapky a jedno pravidlo.
 *
 * Hodnota navenek (`value` / `onValueChange`) je KANONICKA (`1250000.5`) —
 * tentyz desetinny retezec, jaky cte `parseDecimal` v `@reos/money` a jaky
 * uklada API. Vzor je `DateField`: strojova hodnota venku, cesky zapis v poli.
 */

export interface CzechNumberInputProps {
  /** Kanonicka hodnota (`1250000.5`), `''` = nevyplneno. Snese i `numeric(18,4)` z API. */
  readonly value: string;
  /** Dostane kanonickou hodnotu, nebo `''` kdyz uzivatel pole vyprazdnil. */
  readonly onValueChange: (next: string) => void;
  readonly id?: string | undefined;
  readonly className?: string | undefined;
  readonly required?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly testId?: string | undefined;
}

export function CzechNumberInput({
  value,
  onValueChange,
  id,
  className,
  required,
  disabled,
  ariaLabel,
  testId,
}: CzechNumberInputProps): React.JSX.Element {
  const locale = useLocaleOrDefault();
  const t = (key: Parameters<typeof translate>[0]): string => translate(key, { locale });
  /*
   * ROZHODNUTI UXD2: hodnota, kterou clovek NENAPSAL, se v poli nepreformatuje.
   *
   * Pole se plni i ze serveru (zaklad provize ze smlouvy, nedoplatek dluznika,
   * vychozi cena z katalogu). Preformatovat ji hned po dosazeni by znamenalo,
   * ze se obsah pole zmeni bez jakehokoli lidskeho zasahu — a zaroven by to
   * prepsalo to, co u tech poli meri hotove testy (`new-invoice-basis`
   * `5100000`, `rent-pay-amount` `35200.00`). Cesky zapis se tedy ukaze na tom,
   * co clovek do pole napsal (po odchodu z pole), vstup s carkou a mezerami se
   * prijima vzdy.
   */
  const [draft, setDraft] = React.useState(value);
  const [invalid, setInvalid] = React.useState(false);
  /** Napsal do pole clovek? Jen pak se pri odchodu preformatuje. */
  const edited = React.useRef(false);
  /*
   * Synchronizace s props je V RENDERU, ne v `useEffect` — poucenim z
   * `contracts-draft-input` a `DateField`: efekt po pripojeni se v plne brane
   * stihne az po prvnim mereni a pole pak ukaze prazdnou hodnotu.
   */
  const [seenValue, setSeenValue] = React.useState(value);
  /** Dokud v poli stoji kurzor, hodnota z props rozepsany text neprepisuje. */
  const focused = React.useRef(false);
  if (value !== seenValue) {
    setSeenValue(value);
    const read = readCzechNumberInput(draft);
    if (!focused.current && (read.state !== 'ok' || read.value !== canonicalCzechNumber(value))) {
      setDraft(value);
      setInvalid(false);
      edited.current = false;
    }
  }

  const errorId = testId === undefined ? undefined : `${testId}-error`;

  function handleChange(next: string): void {
    setDraft(next);
    edited.current = true;
    if (next.trim() === '') {
      setInvalid(false);
      commit('');
      return;
    }
    const read = readCzechNumberInput(next);
    // `incomplete` = `1 250 000,` uprostred zapisu desetin. Neni to chyba ani
    // hodnota k ulozeni — ceka se na dalsi znak.
    if (read.state === 'incomplete') return;
    setInvalid(read.state === 'invalid');
    if (read.state === 'ok') commit(read.value);
  }

  function commit(next: string): void {
    setSeenValue(next);
    onValueChange(next);
  }

  /** Po odchodu z pole se NAPSANA platna hodnota prepise na cesky zapis (`1250000.5` → `1 250 000,50`). */
  function handleBlur(): void {
    focused.current = false;
    if (!edited.current || draft.trim() === '') return;
    const read = readCzechNumberInput(draft);
    if (read.state !== 'ok') {
      // Rozepsane desetiny (`15 000,`) uz nic nedopise — je to chyba jako kazda jina.
      setInvalid(true);
      return;
    }
    setDraft(formatCzechNumber(read.value));
    if (read.value !== value) commit(read.value);
  }

  return (
    <>
      <input
        // `type="text"` je tu VYSLOVNE, aby bylo v DOM videt, ze pole uz neni
        // nativni `number`; `data-czech-number` to umi overit i z e2e.
        type="text"
        inputMode="decimal"
        autoComplete="off"
        lang="cs"
        id={id}
        className={className}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={invalid ? errorId : undefined}
        title={t('webAdmin.numberField.hint')}
        data-testid={testId}
        data-czech-number="1"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={handleBlur}
      />
      {/* Tataz hlaska i tytez styly jako u ciselneho pole v editoru smlouvy (EDIT2 bod 1). */}
      {invalid && (
        <span className="reos-field-row__error" role="alert" id={errorId} data-testid={errorId}>
          {t('webAdmin.numberField.invalid')}
        </span>
      )}
    </>
  );
}
